/**
 * Password modal — shown ONCE on app boot if no valid token is cached.
 *
 * Drives the auth flow:
 *   1. If stored token is still valid → resolve immediately.
 *   2. Else show the #auth-modal overlay, focus the password input.
 *   3. On submit: POST {authUrl}/auth, on success store + resolve.
 *   4. On 401: show inline error, keep modal up, allow retry.
 *
 * Returns the bearer token. The caller threads it into Colyseus joinOptions
 * and any HTTP requests to /rooms/*. We don't auto-refresh on expiry; the
 * caller re-prompts via clearStoredToken() + a fresh requestPasswordIfNeeded.
 */

import {
  AuthError,
  exchangePassword,
  getStoredToken,
  isTokenExpired,
  setStoredToken,
} from "./auth";

export interface AuthPromptOpts {
  /** Base URL of the engine HTTP API. `/auth` is POSTed against this. */
  authUrl: string;
  /** Override the auth-modal root id (default "auth-modal"). For tests. */
  modalId?: string;
  /** Override the doc to mount onto. For tests. */
  doc?: Document;
}

/**
 * If a valid stored token exists, return it. Otherwise open the modal and
 * resolve once the user successfully authenticates. Rejects only on
 * unrecoverable errors (e.g. malformed DOM); password 401s loop back to the
 * modal.
 */
export async function requestPasswordIfNeeded(opts: AuthPromptOpts): Promise<string> {
  const stored = getStoredToken();
  if (stored && !isTokenExpired(stored)) {
    return stored;
  }

  const doc = opts.doc ?? document;
  const modal = doc.getElementById(opts.modalId ?? "auth-modal");
  if (!modal) {
    throw new Error(
      `auth modal element #${opts.modalId ?? "auth-modal"} not found in DOM`,
    );
  }
  const input = modal.querySelector<HTMLInputElement>("input[name='password']");
  const submit = modal.querySelector<HTMLButtonElement>("button[type='submit']");
  const errorEl = modal.querySelector<HTMLDivElement>(".auth-error");
  const form = modal.querySelector<HTMLFormElement>("form");
  if (!input || !submit || !form || !errorEl) {
    throw new Error("auth modal markup malformed — missing input/button/form/error");
  }

  showModal(modal);
  // Defer focus so the browser actually scrolls/focuses after rendering.
  setTimeout(() => input.focus(), 50);

  return new Promise<string>((resolve) => {
    const setError = (msg: string): void => {
      errorEl.textContent = msg;
      errorEl.style.opacity = msg ? "1" : "0";
    };

    let submitting = false;
    const onSubmit = async (e: Event): Promise<void> => {
      e.preventDefault();
      if (submitting) return;
      const password = (input.value ?? "").trim();
      if (!password) {
        setError("enter the password to continue");
        input.focus();
        return;
      }
      submitting = true;
      submit.disabled = true;
      submit.textContent = "checking…";
      setError("");
      try {
        const { token } = await exchangePassword(opts.authUrl, password);
        setStoredToken(token);
        hideModal(modal);
        form.removeEventListener("submit", onSubmit);
        resolve(token);
      } catch (err) {
        if (err instanceof AuthError) {
          setError(err.message);
        } else {
          setError(
            `server unreachable: ${err instanceof Error ? err.message : String(err)}`,
          );
        }
        submitting = false;
        submit.disabled = false;
        submit.textContent = "enter";
        input.select();
      }
    };

    form.addEventListener("submit", onSubmit);
    // No beforeunload listener: a reject after page-unload only surfaces in
    // the tearing-down tab's console (the new page loads fresh and never
    // sees the rejection), and the noise outweighs the recovery hint.
  });
}

function showModal(el: Element): void {
  el.removeAttribute("hidden");
  (el as HTMLElement).style.display = "flex";
}

function hideModal(el: Element): void {
  el.setAttribute("hidden", "");
  (el as HTMLElement).style.display = "none";
}
